import { ThemeProperties } from "../../Theme/ThemeProperties";
import { DateFormatString } from "../DatePicker/DateFormatters";

const getXAxisFormat = (zoomedRange) => {
    if (!zoomedRange) {
        return "h TT"; // No zoom: show only hours
    }

    const rangeMs = zoomedRange.maxX - zoomedRange.minX;
    const rangeMinutes = rangeMs / (60 * 1000);
    const rangeHours = rangeMs / (60 * 60 * 1000);

    if (rangeMinutes < 5) {
        return "h:mm:ss TT"; // Less than 5 minutes: show hours, minutes, seconds
    } else if (rangeHours < 12) {
        return "h:mm TT"; // Less than 12 hours: show hours and minutes
    } else {
        return "h TT"; // 12 hours or more: show only hours
    }
};

export const getBaseCanvasSettings = (primaryGranularity = 'raw', series = []) => {
    const dynamicColors = series.map(s => s.color || '#000000');
    const strokeWidths = series.map(s => 2);
    return {
        legend: {
            show: false,
            position: 'top'
        },

        colors: dynamicColors,
        stroke: {
            width: strokeWidths,
            curve: 'straight',
        },
        noData: {
            text: undefined,
            align: 'center',
            verticalAlign: 'middle',
            offsetX: 0,
            offsetY: 0,
            style: {
                color: '#999',
                fontSize: '14px',
                fontFamily: 'Roboto',
            }
        },
        markers: {
            size: primaryGranularity === 'raw' ? 0 : 4,
            strokeColors: dynamicColors,
        },
    }
}

export const getBaseChartSettings = (chartID, onZoom = () => {}, setLoading = null) => {
    return {
            id: chartID,
            type: 'line',
            stacked: false,
            height: 360,
            zoom: {
                enabled: true,
                type: 'x',
                allowMouseWheelZoom: false,
            },
            toolbar: { show: false, },
            animations: { enabled: false, },
            events: {
                zoomed: (_chartContext, { xaxis, yaxis }) => {
                    // Set loading state immediately to prevent glitch from Apex's in built zoom
                    if (setLoading) {
                        setLoading(true);
                    }
                    // Trigger the zoom handler with the new range
                    // The zoom handler will set isZoomed state which controls the reset button
                    onZoom?.(xaxis);
                },
            },
    }
}

export const getBaseXAxisSettings = (primaryGranularity, categories, startDate, zoomedRange = null) => {
    const settings = {
        type: "datetime",
        tooltip: {enabled: false},

        labels: {
            show: true,
            datetimeUTC: false,
            rotate: 0,
            rotateAlways: false,
            hideOverlappingLabels: false,
            style: {
                colors: "#87939D",
                fontSize: "12px",
                fontFamily: "Roboto",
                fontWeight: "500",
                LineHeight: "16px",
            },
            offsetX: 0,
            offsetY: -2,
            format: getXAxisFormat(zoomedRange),
        },

        axisTicks: {
            show: false,
            height: 3,
            borderType: "solid",
            color: ThemeProperties.c_axis_labels_color,
        },

        axisBorder: {
            show: true,
            color: ThemeProperties.c_axis_labels_color,
        },

    }

    if (primaryGranularity != 'raw') {
        settings.categories = categories
        settings.tickAmount = 'dataPoints'
        settings.type = 'category'
    } else {
        if (zoomedRange) {
            // Use zoomed range if available
            settings.min = zoomedRange.minX
            settings.max = zoomedRange.maxX
        } else if (startDate) {
            // Use default full day range
            let selectedDate = startDate.value
            settings.min = new Date(DateFormatString(selectedDate)).getTime()
            settings.max = new Date(DateFormatString(selectedDate)).getTime() + (86400000)
        }
    }

    return settings
}

export const getBaseTooltipSettings = () => {
    return {
        shared: true,
        intersect: false,
        marker: { show: true, }
    }
}

export const isBaseDataSeriesEmpty = (series) => {
    if (!series || series.length === 0) {
        return true;
    }

    if (!series[0].data) {
        return true;
    }

    const isEmpty = !series.some(item =>
        item.data.some(point => point !== null || point?.y !== null)
    );

    return isEmpty;
}